/* eslint-disable no-empty */

import { DEFAULT_EMPTY_CURRENCY_ALPHA3, DEFAULT_EMPTY_CURRENCY_PRECISION, noMethodsError } from 'store/constants';
import type {
  BonusInfoResponse,
  LostProfitInfo,
  Parameter,
  PayGroupCategories,
  PayHubCashierContractsInitParamsResponse,
  PayHubCashierContractsLostProfitMerchantBonus,
  PayHubCashierContractsLostProfitMerchantBonusTier,
  PayHubCashierContractsPaymentGroupInfo as GroupInfo,
  PayHubCashierContractsPaymentsInitPaymentGroupsCurrencyResponse as Currency,
  PayHubCashierContractsPaymentsInitPaymentGroupsResponse as InitSuccess,
  PayHubWidgetContractsPayoutsSelectPayoutGroupResponse,
  PaymentProcessMethodInfo,
  UserBonusInfo,
} from 'store/models';
import {
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamType,
  PayHubAdminPanelDomainEnumsParameterDataType,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType as ActionTypes,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType,
} from 'store/models';
import {
  DISABLED_CATEGORY_KEY,
  filterMethodsByPayType,
  getViewData,
  parseQuery,
  stringToInt,
  transformContentUrl,
} from 'utils';
import { isMethodAvailable } from 'utils/getIsMethodActive';
import { isPwContext } from 'utils/pwContext';
import { getSelectedCurrency, mapToCurrencyBase } from 'utils/wallet';

import type { GetDepositMethodsRequestProps } from '../services/depositAPI';
import type { GeneralInfo, UIPayment, UIPaymentGroups, ViewDataType } from './commonPayments';
import {
  formNotificationData,
  getCarouselMethodsAndMethodToRedirect,
  getCurrency,
  getEnabled,
  getIsLimitReached,
  getMaxAmount,
  getMethodsTypes,
  getMinLimit,
  sortByArrangement,
} from './commonPayments';

export const lostProfitLocizeKeys = {
  SINGLE_TIER: {
    ONLY_PERCENTS: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_FIXED_PERCENT',
    ONLY_ABSOLUTE: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_FIXED_VALUE',
    PERCENTS_AND_ABSOLUTE: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_PERCENT_VALUE',
  },
  MULTIPLE_TIERS: {
    MAX_PERCENTS: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_MAX_PERCENT',
    MAX_ABSOLUTE: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_MAX_VALUE',
    MAX_PERCENTS_AND_MAX_ABSOLUTE: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.RIBBON_MAX_PERCENT_VALUE',
  },
  LOST_PROFIT_METHODS: {
    HEADER: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.HEADER',
    INFO: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.INFO',
    AMT: 'CASHIER.PH2.LOST_PROFIT_MERCHANT_BONUS.AMT',
  },
};

export const calculateBiggest = (tiers: PayHubCashierContractsLostProfitMerchantBonusTier[]) => {
  let biggestPercentValue = 0;
  let biggestAbsoluteValue = 0;

  tiers.forEach((tier) => {
    if (tier.percent_value && tier.percent_value > biggestPercentValue) {
      biggestPercentValue = tier.percent_value;
    }
    if (tier.absolute_value && tier.absolute_value > biggestAbsoluteValue) {
      biggestAbsoluteValue = tier.absolute_value;
    }
  });

  return {
    percent_value: biggestPercentValue,
    absolute_value: biggestAbsoluteValue,
  };
};

export const generateLabelForLostProfit = (
  method: PayHubCashierContractsLostProfitMerchantBonus,
  currencyName: string,
): LostProfitInfo => {
  const isSingleTier = method.tiers.length === 1;
  const isMultipleTiers = method.tiers.length > 1;
  const isOnlyPercentProfit = method.tiers.every((item) => item.absolute_value === 0);
  const isOnlyAbsoluteValue = method.tiers.every((item) => item.percent_value === 0);
  const isZeroValue = method.tiers.every((item) => item.percent_value === 0 && item.absolute_value === 0);
  const labelProps = {
    ...calculateBiggest(method.tiers),
    currency: currencyName,
  };

  if (isSingleTier && !isZeroValue) {
    if (isOnlyPercentProfit) {
      return {
        text: lostProfitLocizeKeys.SINGLE_TIER.ONLY_PERCENTS,
        props: labelProps,
      };
    }

    if (isOnlyAbsoluteValue) {
      return {
        text: lostProfitLocizeKeys.SINGLE_TIER.ONLY_ABSOLUTE,
        props: labelProps,
      };
    }

    return {
      text: lostProfitLocizeKeys.SINGLE_TIER.PERCENTS_AND_ABSOLUTE,
      props: labelProps,
    };
  }

  if (isMultipleTiers && !isZeroValue) {
    if (isOnlyPercentProfit) {
      return {
        text: lostProfitLocizeKeys.MULTIPLE_TIERS.MAX_PERCENTS,
        props: labelProps,
      };
    }

    if (isOnlyAbsoluteValue) {
      return {
        text: lostProfitLocizeKeys.MULTIPLE_TIERS.MAX_ABSOLUTE,
        props: labelProps,
      };
    }
    return {
      text: lostProfitLocizeKeys.MULTIPLE_TIERS.MAX_PERCENTS_AND_MAX_ABSOLUTE,
      props: labelProps,
    };
  }
  return {
    text: '',
    props: {
      percent_value: 0,
      absolute_value: 0,
      currency: '',
    },
  };
};

export type LostProfitCompareResult = {
  isLostProfit: boolean;
  label: LostProfitInfo | null;
};

export const getLostProfitCompareResult = (
  currentMethod: GroupInfo,
  lostProfitRawData?: PayHubCashierContractsLostProfitMerchantBonus[] | null,
): LostProfitCompareResult => {
  const defaultReturn = {
    isLostProfit: false,
    label: null,
  };
  try {
    if (lostProfitRawData) {
      return lostProfitRawData.reduce(
        (acc: LostProfitCompareResult, currItem: PayHubCashierContractsLostProfitMerchantBonus) => {
          if (currItem.pay_group.toLowerCase() === currentMethod.short_name!.toLowerCase()) {
            const [currency] = currentMethod.currencies as Currency[];
            const currencyName = currency.currency!.alpha3 as string;
            return {
              isLostProfit: true,
              label: generateLabelForLostProfit(currItem, currencyName),
            };
          }
          return acc;
        },
        defaultReturn,
      );
    }
  } catch {}

  return defaultReturn;
};

const formDepositMethod =
  (generalInfo: GeneralInfo, paygroupCategories?: PayGroupCategories) =>
  (pmMethod: GroupInfo): UIPaymentGroups => {
    const { isLimitReached, generalViewData, lost_profit_merchant_bonus, userBonuses } = generalInfo;
    const { display_limits = false } = pmMethod;
    const currentCurrency = getCurrency(pmMethod);
    const min = getMinLimit(currentCurrency);
    const max = getMaxAmount(currentCurrency);
    const lostProfitCompareResult = getLostProfitCompareResult(pmMethod, lost_profit_merchant_bonus);

    // now we use only first bonus in array user_bonuses BILL-16033
    const bonusesPayGroups = (userBonuses && userBonuses.length > 0 && userBonuses[0].bonusPayGroupsIds) || [];

    const infoLink = pmMethod.info;
    const methodId = pmMethod.id || 0;
    const isIncludeBonus = Boolean(bonusesPayGroups.includes(methodId));
    const precisionValue = currentCurrency.currency?.precision;
    const currencyPrecision =
      precisionValue != null && Number(precisionValue) <= DEFAULT_EMPTY_CURRENCY_PRECISION
        ? Number(precisionValue)
        : DEFAULT_EMPTY_CURRENCY_PRECISION;
    const isIncludedInDisabledCategory = Boolean(
      paygroupCategories?.categories
        .find((c) => c.category_name === DISABLED_CATEGORY_KEY)
        ?.pay_group_ids?.includes(methodId),
    );

    return {
      arrangement: pmMethod.arrangement || 0,
      id: pmMethod.id || 0,
      name: pmMethod.short_name?.trim() || '',
      displayName: pmMethod.public_name || '',
      isPopular: Boolean(pmMethod.popular),
      logoUri:
        pmMethod.logo && pmMethod.logo.startsWith('/') && isPwContext() ? `/pw${pmMethod.logo}` : pmMethod.logo || '',
      nativePayType: pmMethod.native_pay_type || '',
      paymentType: '',
      requiredFields: [],
      currencyId: currentCurrency.currency?.currency_id || 0,
      currencyAlpha3: currentCurrency.currency?.alpha3 || '',
      currencyPrecision,
      descriptionUrl: infoLink ? transformContentUrl(infoLink) : '',
      limits: {
        min,
        max,
        displayLimits: display_limits,
      },
      amountRegExpFe: '',
      viewData: [],
      payGroupActionType: pmMethod.action_type || '',
      fee: currentCurrency.fee || '',
      merchantUserFee: pmMethod.merchant_user_fee,
      processingTimeTranslationKey: currentCurrency.processing_time_translation_key || undefined,
      showProcessingTime: currentCurrency.show_processing_time || false,
      isSelfTarget: pmMethod.info_in_iframe || false,
      enabled: getEnabled(isLimitReached, min, pmMethod, generalViewData, false),
      disabledByCategory: isIncludedInDisabledCategory,
      restrictedUntil: pmMethod?.restricted_until,
      pay_method_type: pmMethod.pay_method_type || '',
      last_used: pmMethod.last_used || false,
      autoSubmit: pmMethod.auto_submit || false,
      isCheckout: false,
      preSelectedMethod: pmMethod.allow_pre_selected_method || false,
      isLostProfit: lostProfitCompareResult.isLostProfit || false,
      lostProfitInfo: lostProfitCompareResult.label || null,
      isLoading: true,
      showInCarousel: pmMethod.show_in_carousel || false,
      isIncludeBonus,
      walletConnect: pmMethod.wallet_connect || false,
      linkedWalletConnectGroup: pmMethod.linked_wallet_connect_group,
    };
  };

const shouldBeRedirectedToMethod = (lastUsedMethod?: UIPaymentGroups) =>
  lastUsedMethod?.payGroupActionType === 'activity' &&
  isMethodAvailable(lastUsedMethod) &&
  lastUsedMethod.preSelectedMethod;

export const getMethodForAutoRedirect = (props: {
  methods: UIPaymentGroups[];
  initCarouselMethods: UIPaymentGroups[];
  payGroupId?: number;
  skipRedirectMethod: boolean;
}): UIPaymentGroups | undefined => {
  const { methods = [], initCarouselMethods = [], payGroupId, skipRedirectMethod } = props;

  if (methods.length === 0 || skipRedirectMethod) {
    return undefined;
  }

  const [firstMethod] = methods;

  const isAutoRedirectable = (m?: UIPaymentGroups) => m?.payGroupActionType !== ActionTypes.NestedList;

  if (methods.length === 1 && isMethodAvailable(firstMethod) && isAutoRedirectable(firstMethod)) {
    return firstMethod;
  }

  const { pay_group = '' } = parseQuery(window.location.search);
  const methodFromQuery = methods.find(({ id, name }) => id === stringToInt(pay_group as string) || name === pay_group);

  if (methodFromQuery && isMethodAvailable(methodFromQuery) && isAutoRedirectable(methodFromQuery)) {
    return methodFromQuery;
  }

  if (payGroupId) {
    const method = methods.find((m) => m.id === payGroupId);
    if (isAutoRedirectable(method)) return method;
  }

  const lastUsedMethod = methods.find((m) => m.last_used);
  if (shouldBeRedirectedToMethod(lastUsedMethod)) {
    return lastUsedMethod;
  }

  if (initCarouselMethods.length > 1 && isAutoRedirectable(initCarouselMethods[0])) {
    return initCarouselMethods[0];
  }

  return undefined;
};

const formatUserBonuses = (methods: GroupInfo[], bonuses?: BonusInfoResponse[]): UserBonusInfo[] | null => {
  if (!bonuses) return null;

  return bonuses.map((bonus) => {
    const bonusPayGroups = bonus.pay_groups || [];
    const bonusPayGroupsIds = methods
      .filter((item) => bonusPayGroups.includes(item.short_name || ''))
      .map((item) => item.id || 0);
    return {
      bonusName: bonus.bonusName,
      promotion_id: bonus.promotion_id,
      bonusImageUrl: bonus.bonus_image_url,
      is_selected: bonus.is_selected,
      show_info_line: bonus.show_info_line,
      info_line: bonus.info_line,
      bonusDescription: bonus.bonusDescription,
      minDepositAmount: bonus.minDepositAmount,
      maxDepositAmount: bonus.maxDepositAmount,
      absoluteBonusValue: bonus.absoluteBonusValue,
      percentBonusValue: bonus.percentBonusValue,
      payGroups: bonus.pay_groups,
      maxBonusValue: bonus.maxBonusValue,
      productType: bonus.productType,
      tiers: bonus.tiers,
      overrideBonusDepositAmount: bonus.overrideBonusDepositAmount,
      bonusPayGroupsIds,
    };
  });
};

export const formDepositGroups = (
  payload?: InitSuccess,
  args?: GetDepositMethodsRequestProps,
  userAgent?: string,
  appChannel?: string,
  initCurrency?: string,
): UIPayment => {
  const {
    payGroupId = undefined,
    skipRedirectMethod = false,
    isOnRamp = false,
    hasOnRampCurrency: hasInitialOnRamp = false,
  } = args || {};

  if (!payload) {
    return {
      tax: undefined,
      methodsTypes: [],
      isReady: false,
      canAutoRedirect: true,
      isRequestPending: false,
      submitPaymentReady: false,
      currencyId: undefined,
      currencyAlpha3: undefined,
      widgetSettings: {
        country_filtering_enabled: false,
      },
      growthbookFeatures: undefined,
      methods: [],
      carouselMethodList: [],
      methodToRedirect: undefined,
      viewData: [],
      withdrawalReverse: undefined,
      merchantUrl: undefined,
      merchantSuccessUrl: undefined,
      merchantFailUrl: undefined,
      merchantProcessingUrl: undefined,
      merchantRedirectType: undefined,
      userBonuses: null,
      userBalance: 0,
      authToken: '',
      merchantApiKey: '',
      paygroupCategories: undefined,
      selectedCurrency: null,
      defaultFiatCurrency: null,
      shouldSkipInitFeature: false,
      walletCurrencies: [],
      meshMethods: [],
      userCurrency: null,
      userBlocked: false,
    };
  }

  const {
    view_data,
    payment_groups = null,
    cashier_settings = {},
    cashier_features = [],
    lost_profit_merchant_bonus = [],
    withdrawal_reverse,
    tax,
    user_bonuses,
    user_currency,
    user_blocked,
    merchant_url,
    merchant_success_url,
    merchant_fail_url,
    merchant_processing_url,
    merchant_redirect_type,
    // NOTICE: user_balance always return, it is not nullable
    user_balance = 0,
    default_fiat_currency,
    auth_token = null,
    merchant_api_key = null,
    merchant_paygroup_category,
    wallet_currencies = [],
  } = payload;
  const billingFeatures = (cashier_features || []).filter((f) => f.startsWith('billing'));
  const groups = Array.isArray(payment_groups) ? payment_groups : [];
  /**
   * Filtering by 'onRamp' type is required because the /init endpoint
   * always returns all available payment groups for the selected currency.
   *
   * In multi-currency mode:
   * - If there is no onRamp currency — all groups are shown.
   * - If there is an onRamp currency:
   *   - On OnRamp flow → only 'onRamp' providers are shown.
   *   - On non-OnRamp flow → 'onRamp' providers are excluded.
   *
   * In non-multi-currency mode — all groups are shown.
   */
  const hasOnRampCurrency = hasInitialOnRamp || (wallet_currencies?.some((currency) => currency.is_on_ramp) ?? false);
  const { filteredGroups, meshMethods } = groups.reduce(
    (acc, group) => {
      const isOnrampType = group.action_type === PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Onramp;
      const shouldInclude =
        !group.wallet_connect &&
        (!cashier_settings.multi_currency_cashier || !hasOnRampCurrency || isOnrampType === isOnRamp);
      if (shouldInclude) acc.filteredGroups.push(group);
      if (group.wallet_connect) acc.meshMethods.push(group);
      return acc;
    },
    {
      filteredGroups: [] as GroupInfo[],
      meshMethods: [] as GroupInfo[],
    },
  );
  const preparedViewData = (
    filteredGroups?.length ? view_data : [noMethodsError, ...(view_data || [])]
  ) as ViewDataType;
  const isLimitReached = getIsLimitReached(preparedViewData);
  const formattedBonuses = formatUserBonuses(filteredGroups, user_bonuses);

  const generalSettings: GeneralInfo = {
    isLimitReached,
    generalViewData: preparedViewData,
    lost_profit_merchant_bonus,
    userBonuses: formattedBonuses,
  };
  const methods = [...filteredGroups]
    .sort(sortByArrangement)
    .map(formDepositMethod(generalSettings, merchant_paygroup_category));
  const formattedMeshMethods = meshMethods
    .map(formDepositMethod(generalSettings, merchant_paygroup_category))
    .filter(({ enabled }) => enabled);
  const methodsByPayType = filterMethodsByPayType(methods as UIPaymentGroups[], userAgent, appChannel);
  const currencyId = methodsByPayType && methodsByPayType[0]?.currencyId;
  const currencyAlpha3 = user_currency || (methodsByPayType && methodsByPayType[0]?.currencyAlpha3);
  const methodsTypes = getMethodsTypes(methodsByPayType);
  const getMethodForAutoRedirectCallback = (initCarouselMethods: UIPaymentGroups[]) =>
    getMethodForAutoRedirect({
      methods: methodsByPayType,
      initCarouselMethods,
      payGroupId,
      skipRedirectMethod,
    });
  const { methodToRedirect, carouselMethodList } = getCarouselMethodsAndMethodToRedirect<UIPaymentGroups>(
    methodsByPayType,
    getMethodForAutoRedirectCallback,
  );
  const selectedCurrency = getSelectedCurrency({
    walletCurrencies: wallet_currencies,
    userCurrency: user_currency || null,
    groups: methods,
    initCurrency,
  });
  const isCryptoMultiCashier = cashier_settings.multi_currency_cashier && selectedCurrency?.isFiat === false;
  const shouldSkipInitFeature = methods.length !== 0 && !user_blocked && isCryptoMultiCashier && !isOnRamp;
  const formatedCurrencies = wallet_currencies.map(mapToCurrencyBase);

  return {
    tax,
    methodsTypes,
    isReady: true,
    canAutoRedirect: true,
    isRequestPending: false,
    submitPaymentReady: false,
    currencyId,
    currencyAlpha3,
    widgetSettings: cashier_settings,
    growthbookFeatures: billingFeatures,
    methods: methodsByPayType,
    carouselMethodList,
    methodToRedirect,
    viewData: formNotificationData({
      viewData: preparedViewData as ViewDataType,
      withdrawalReverse: withdrawal_reverse,
      isGeneral: true,
    }),
    withdrawalReverse: withdrawal_reverse,
    merchantUrl: merchant_url,
    merchantSuccessUrl: merchant_success_url,
    merchantFailUrl: merchant_fail_url,
    merchantProcessingUrl: merchant_processing_url,
    merchantRedirectType: merchant_redirect_type,
    userBonuses: formattedBonuses,
    userBalance: user_balance,
    userCurrency: user_currency || null,
    authToken: auth_token || '',
    merchantApiKey: merchant_api_key || '',
    paygroupCategories: merchant_paygroup_category,
    walletCurrencies: formatedCurrencies || [],
    shouldSkipInitFeature,
    selectedCurrency,
    defaultFiatCurrency: default_fiat_currency || null,
    meshMethods: formattedMeshMethods,
    userBlocked: user_blocked || false,
  };
};

export const formDepositRequiredFields = (
  parameters: PayHubCashierContractsInitParamsResponse[] | null | undefined,
): Parameter[] | [] => {
  if (!parameters || parameters.length === 0) {
    return [];
  }

  return parameters.map(
    ({
      name,
      type,
      extended_type,
      data_type,
      regexp,
      label,
      place_holder,
      place_holder_empty_list,
      place_holder_empty_list_error,
      is_required,
      is_otp_requisites,
      options,
      user_requisite,
      validation_msg_key,
      is_read_only,
      checksum_validation,
    }: PayHubCashierContractsInitParamsResponse): Parameter => {
      const formattedOptions =
        options && options.length
          ? options.map((option) => ({
              ...option,
              selected: option.is_selected || false,
              name: option.label || '',
              value: option.value || '',
            }))
          : undefined;

      return {
        name: name || '',
        key: name || JSON.stringify(label),
        type: type || PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamType.Input,
        extendedType: extended_type,
        dataType: data_type || PayHubAdminPanelDomainEnumsParameterDataType.String,
        regexp: regexp || undefined,
        label,
        placeHolder: place_holder,
        placeHolderEmptyList: place_holder_empty_list,
        placeHolderEmptyListError: place_holder_empty_list_error,
        isRequired: is_required,
        isOtpRequisites: is_otp_requisites,
        options: formattedOptions,
        userRequisite: user_requisite || null,
        validationMsgKey: validation_msg_key,
        isDisabled: is_read_only,
        checksumValidation: checksum_validation,
      };
    },
  );
};

export const formDepositMethodInfo = (
  response: PayHubWidgetContractsPayoutsSelectPayoutGroupResponse,
  requiredFieldsFormatter: (params: PayHubCashierContractsInitParamsResponse[] | null | undefined) => Parameter[],
  payGroupId: number,
): PaymentProcessMethodInfo => {
  const {
    currency = {},
    limits = {},
    amount_blocks,
    parameters,
    view_data,
    processing_currency,
    show_processing_currency,
    amount_reg_exp_fe,
    auto_submit,
    preset_behavior,
    preset_amount,
    preset_currency,
    allow_preset_amount,
    merchant_user_fee,
    processing_currency_rate,
    processing_time_translation_key,
    workflow_type,
    info,
    fee,
    is_routable,
    contains_cached_result = false,
    amount_can_be_changed,
    possible_changes_amount,
  } = response;
  const { currency_id, alpha3 } = currency;
  const { min_amount, max_amount, max_recurring_amount } = limits;

  const limitsObject = {
    min: min_amount || 0,
    max: max_amount || undefined,
    maxRecurrentAmount: max_recurring_amount || undefined,
  };
  const requiredFields = requiredFieldsFormatter(parameters);
  const { additionalViewData, viewData } = getViewData(view_data);

  return {
    showProcessingTime: show_processing_currency as boolean,
    currencyAlpha3: alpha3 || DEFAULT_EMPTY_CURRENCY_ALPHA3,
    limits: limitsObject,
    currencyId: currency_id || 0,
    requiredFields,
    additionalViewData,
    viewData: formNotificationData({
      viewData,
      payGroupId,
    }),
    isCheckout: workflow_type?.toLowerCase() === 'host-to-host',
    isDisplayForm: workflow_type === 'display-form',
    processingCurrency: processing_currency,
    amountBlocks: amount_blocks,
    amountRegExpFe: amount_reg_exp_fe || undefined,
    autoSubmit: auto_submit || preset_behavior?.toLowerCase() === 'autosubmit' || contains_cached_result,
    isAutoSubmitSelectType: Boolean(auto_submit || preset_behavior?.toLowerCase() === 'autosubmit'),
    presetBehavior: preset_behavior,
    presetAmount: preset_amount,
    presetCurrency: preset_currency,
    allowPresetAmount: allow_preset_amount,
    merchantUserFee: merchant_user_fee,
    processingCurrencyRate: processing_currency_rate || undefined,
    info: info || undefined,
    processingTimeTranslationKey: processing_time_translation_key || undefined,
    fee: fee || undefined,
    isRoutable: is_routable,
    isCachedResult: contains_cached_result,
    amountCanBeChanged: amount_can_be_changed || false,
    possibleChangesAmount: possible_changes_amount ? Number(possible_changes_amount) : 0,
  };
};
